import type { SidebarTabItem } from '@/helpers/types/SidebarTabItem';
import type { DraftKey } from '@/stores/draftKey';
import { isDraftColumnId, type CascadeColumn } from '@/cascade/types';

import { usePreferSidebars } from '@/composables/usePreferSidebars';
import { getRouter } from '@/const/routerHandle';
import { draftDefinition } from '@/stores/draftDefinitions';
import { draftZoneAvailable } from '@/stores/draftGraduationPolicy';
import { idPropOf, sidebars, type SidebarType, type SidebarTypeMap } from '@/registries/sidebars';
import { runAfterModalUnwind } from '@/composables/runAfterModalUnwind';
import { useCascadeStore } from '@/stores/cascadeStore';
import { useDraftStore } from '@/stores/draftStore';
import { isWorkspaceRoute } from '@/stores/workspaceRoute';
import { defineStore } from 'pinia';
import { Observable, Subject, filter } from 'rxjs';
import { computed, ref, toRaw, type ComputedRef, type Ref } from 'vue';

export class Sidebar<T extends SidebarType> {
  closing = false;
  activeTabView = '';
  // Set by CascadeColumn when this Sidebar is a mount-time mirror of a column
  // in the URL-driven cascade — keeps overlay rendering and history-intercept
  // from double-handling URL-served panels.
  inline = false;
  // Live header title resolved by the panel's GenericSidebarTemplate (the entity
  // name, e.g. "PINO Hamburg"). Published so a child column's breadcrumb can echo
  // the parent column's header instead of the static registry title. Empty until
  // the panel resolves a headline (then the breadcrumb falls back to the title).
  displayTitle = '';
  // Whether the panel holds unsaved changes, published by its
  // GenericSidebarTemplate (ADR-0029 §D). Read by both close paths — the inline
  // header ✕ and the overlay click-outside — so "ask only when there is something to
  // lose" is decided in one place by the panel that knows. `undefined` means the
  // panel does not report, and the close guard falls back to always asking.
  dirty?: boolean;
  // Set by DraftZoneColumn when this Sidebar mirrors a draft-zone instance
  // (ADR-0029 §G) rather than a cascade column or an overlay. It is the instance's
  // id, which is also its key in `draftStateStore` — so this one field tells
  // `closeSidebar` to retire an instance instead of navigating the cascade, and
  // tells the panel's header which draft it is asking about.
  draftInstanceId?: DraftKey;
  // Set by DraftZoneChild: the id of the draft this panel is shown beside. It is not
  // a draft itself, so closing it retires only this panel — never the draft.
  draftChildOf?: DraftKey;
  // The answer `askSidebar` is waiting for, settled by this panel's close. Only an
  // asked overlay carries one: every other open has nobody waiting on its close.
  answer?: Promise<SidebarCloseEvent<SidebarType>>;
  settle?: (event: SidebarCloseEvent<SidebarType>) => void;
  constructor(
    public readonly sidebarType: T,
    // Not readonly: an inline column mirror keeps `props` in sync when its bound
    // id changes in place (same type at the same cascade index, no remount — the
    // `type:index` keying case). `closeSidebar` matches the cascade column by
    // `props[idProp]`, so a stale snapshot would close the wrong column.
    public props: SidebarTypeMap[T]['props'],
    // Not readonly: an inline column mirror re-syncs its tab items when they are
    // entity-dependent and resolve after the column mounts (e.g. `staffFile`,
    // whose tabs are gated by the fetched staff's role, or a same-type id swap
    // that doesn't remount). CascadeColumn keeps this in sync from the reactive
    // `resolveInlineTabItems` computed.
    public sidebarTabItems: SidebarTabItem[],
    public backButtonActive?: boolean,
  ) {}
}

export interface SidebarOpenEvent<T extends SidebarType> {
  type: T;
  props?: SidebarTypeMap[T]['props'];
  sidebarTabItems?: SidebarTabItem[];
  initialTabView?: string;
  // Fan-out slot key (ADR-0027 §B, RS-007). When an inline panel opens as a
  // cascade column, this identifies WHICH source list within the parent column
  // it was opened from — the second half of the `(parentColumn, sourceListKey)`
  // slot identity. Lists that want to hold their child open alongside a sibling
  // list's child (e.g. a patient's Behandlungsplan next to a Termin) pass a
  // stable key each; omitting it uses the parent's default (unnamed) slot, which
  // is the degenerate one-child-per-parent case every non-fan-out workspace uses.
  sourceListKey?: string;
  // Force the column to open as the cascade ROOT (re-root), ignoring `parentIds`
  // matching (ADR-0027 §C). A page-level root LIST sets this so selecting an item
  // clears the board and starts fresh — even when a column whose type is one of
  // this panel's declared `parentIds` is open downstream. Without it, a type that
  // is both a workspace root AND a cross-link child (e.g. `patientFile`, opened
  // from the patient list but also FROM an appointment/plan/invoice detail per
  // US7) would wrongly dock under its own descendant instead of re-rooting.
  openAtRoot?: boolean;
  // Re-root the board to this ancestor column, then dock THIS panel under it, in
  // one navigation. For root-list affordances that name a NESTED record — e.g. a
  // treatment-plan / exercise-program pill on a patient row: clicking it should
  // show that patient's file (re-rooted) with the plan/program already open as a
  // child (`list | patientFile(N) | detail(N)`), not the bare detail
  // (`openAtRoot`) nor the detail docked under whatever unrelated patient is
  // already open (plain `parentIds` matching). The ancestor gets the panel's
  // declared `parentTab` so its source list is the shown/highlighted tab. On a
  // non-workspace host the panel stays an overlay and this is ignored.
  reRootUnder?: { type: SidebarType; id: string };
  // Dock under THIS column — the one the opener sits in — rather than under the
  // deepest column of any `parentIds` type. A picker inside the composer that opens
  // "Patient anlegen" means its own composer, even while a Verordnung column of a
  // matching type is open further right; docked there, the new column would bind
  // the Verordnung's id as its origin and answer the wrong form. Falls back to the
  // usual matching when that column is not on the board.
  dockUnder?: { type: SidebarType; id: string };
}

export interface SidebarUpdateEvent<T extends SidebarType> {
  type: T;
  backButtonActive: boolean;
}

export interface SidebarCloseEvent<T extends SidebarType> {
  type: T;
  action: 'cancel' | 'confirm';
  sidebarResult?: SidebarTypeMap[T] extends { sidebarResult: infer R } ? R : never;
  // On the way in: when set, the store closes this specific Sidebar instance
  // instead of the first-of-type match. Inline cascade panels pass their own
  // mirror (injected via ColumnSidebarKey) so two columns sharing a type with
  // different ids close the right one (PRD #971 / RS-001). On the way out it is
  // always set: the panel that closed, whose props `onSidebarAnswer` matches.
  sidebar?: Sidebar<T>;
}

type SidebarProps<T extends SidebarType> = NonNullable<SidebarTypeMap[T]['props']>;

type SidebarStore = {
  openSidebar: <T extends SidebarType>(event: SidebarOpenEvent<T>) => void;
  /**
   * Open a panel for an answer, and get the answer back from its close.
   *
   * ```ts
   * const event = await sidebarStore.askSidebar({ type: 'inviteUser' });
   * if (event.action === 'confirm') pickStaff(event.sidebarResult?.email);
   * ```
   *
   * Always an overlay: a column lives in the URL and a draft in the zone, and
   * either can come back after the call that opened it is gone — a reload, a
   * draft restored on another visit — with nobody left to hand the answer to.
   * A panel that has to open as a column answers through `onSidebarAnswer`
   * instead.
   *
   * Resolves exactly once, with the close of the panel this call pushed and
   * nobody else's, and keeps holding after the caller unmounts. Asking again
   * while the same panel is the one on screen returns the pending answer
   * rather than stacking a second panel.
   */
  askSidebar: <T extends SidebarType>(event: SidebarOpenEvent<T>) => Promise<SidebarCloseEvent<T>>;
  closeSidebar: <T extends SidebarType>(event: SidebarCloseEvent<T>) => void;
  /**
   * The closes of panels opened on behalf of `origin`: those of `type` whose props
   * hold every value in `origin` — `undefined` meaning "not set".
   *
   * For a panel that can outlive the call that opened it — a column restored from
   * the URL, a draft parked in the zone. Its props travel with it, so the origin
   * written into them at open time still names the opener when the panel closes,
   * however many remounts later. A listener subscribed again after a remount hears
   * it; a sibling opener with another origin does not.
   *
   * ```ts
   * sidebarStore.onSidebarAnswer('exerciseLibrary', { programId: props.program.id })
   * ```
   */
  onSidebarAnswer: <T extends SidebarType>(
    sidebarType: T,
    origin: Partial<SidebarProps<T>>,
  ) => Observable<SidebarCloseEvent<T>>;
  /**
   * Every close of `type`, whoever opened the panel. For reacting to the close
   * itself — clearing a selection, resetting a view. Never for the panel's
   * answer: a sibling's close arrives here too, so the answer belongs to
   * `askSidebar` or `onSidebarAnswer`. A refresh after a save belongs in the
   * panel's mutation, which knows what it changed.
   */
  onSidebarClose: <T extends SidebarType>(sidebarType: T) => Observable<SidebarCloseEvent<T>>;
  isSidebarOpen: (sidebarType: SidebarType) => boolean;
  resolveSidebarForClose: (
    sidebarType: SidebarType,
    columnId?: string,
  ) => Sidebar<SidebarType> | undefined;
  sidebarStack: Ref<Sidebar<SidebarType>[]>;
  anySidebarOpen: ComputedRef<boolean>;
  elevatedOverlayOpen: ComputedRef<boolean>;
  updateSidebar: <T extends SidebarType>(event: SidebarUpdateEvent<T>) => void;
  onBackClick: () => Observable<SidebarType>;
  backTrigger: Subject<SidebarType>;
  setSidebarView: (view: string) => void;
  sidebarTabView: Ref<string>;
};

export const useSidebarStore = defineStore('sidebar', (): SidebarStore => {
  const sidebarStack = ref<Sidebar<SidebarType>[]>([]);
  const backTrigger: Subject<SidebarType> = new Subject<SidebarType>();
  const closeTrigger: Subject<any> = new Subject<any>();
  const sidebarTabView = ref<string>('');

  const anySidebarOpen = computed(() => {
    return sidebarStack.value.length > 0;
  });

  /**
   * Whether an `elevated` overlay panel is on screen — i.e. the sidebar layer
   * currently occupies the band ABOVE the anchored peek popper (z-57 vs z-55,
   * see `SidebarComponent.elevated`). A peek that opens while this holds has to
   * be lifted over that band or it renders behind the panel it was raised from,
   * scrim included — the discard prompt of `createPatient` / `inviteUser` is
   * exactly that case. See `GenericModal`'s `aboveElevatedSidebar`.
   *
   * Only the overlay presentation counts: a mirrored cascade column (`inline`)
   * renders in the strip, which a peek already floats above.
   */
  const elevatedOverlayOpen = computed(() =>
    sidebarStack.value.some(
      (s) =>
        !s.closing && !s.inline && sidebars.find((entry) => entry.type === s.sidebarType)?.elevated,
    ),
  );

  const isSidebarOpen = (sidebarType: SidebarType) => {
    return sidebarStack.value
      .filter((s) => !s.closing)
      .map((s) => s.sidebarType)
      .includes(sidebarType);
  };

  const getSidebar = (sidebarType: SidebarType) => {
    return sidebarStack.value.find((s) => s.sidebarType === sidebarType);
  };

  // Resolve the exact instance to close. Prefer the inline cascade column whose id
  // (props[idProp], equal to the cascade column id) matches `columnId`, so a
  // cascade holding two columns of the same sidebarType closes the clicked one and
  // not the first stack match (PRD #971 / RS-001). Falls back to first-of-type when
  // no id is given (overlay close path) or none matches. Mirrors the (type, id)
  // matching closeSidebar itself uses to locate the cascade column.
  const resolveSidebarForClose = (sidebarType: SidebarType, columnId?: string) => {
    if (columnId === undefined) return getSidebar(sidebarType);
    const idProp = sidebars.find((s) => s.type === sidebarType)?.inline?.idProp;
    if (!idProp) return getSidebar(sidebarType);
    return (
      sidebarStack.value.find(
        (s) =>
          s.sidebarType === sidebarType &&
          (s.props as Record<string, unknown> | null)?.[idProp] === columnId,
      ) ?? getSidebar(sidebarType)
    );
  };

  const updateSidebar = <T extends SidebarType>(event: SidebarUpdateEvent<T>) => {
    const sideBar = sidebarStack.value.find((sb) => sb.sidebarType === event.type);
    if (!sideBar) {
      return;
    }
    // Mutate in place rather than replacing the instance: the stack elements are
    // reactive, so assigning the field still triggers reactivity, and keeping the
    // same identity lets consumers (e.g. useHistoryIntercept) track a sidebar's
    // pushed history entries across updates.
    sideBar.backButtonActive = event.backButtonActive;
  };

  /**
   * "Neu" on a `zone` type, while the one active `:new` column already holds work
   * (ADR-0029 §G).
   *
   * The active column is the single create slot: it lives in the URL, it is
   * deep-linkable, and a second click on an UNTOUCHED one is answered by the
   * reducer's own re-trigger — no second column, no code here. What this handles is
   * the other case: the slot is occupied by work the user wants kept. It graduates
   * that column into the draft zone and frees the slot, then reports the promise
   * that has to settle before the fresh column may open.
   *
   * Returns the event to open instead, or `null` when the click needs nothing special.
   *
   * Two rewrites, and both are about the id the affordance passes. Every "Neu" button
   * passes the bare `new` sentinel, which stops matching the open column the moment a
   * generation exists:
   *
   * - **Occupied by work** → graduate it and open the NEXT generation. The fresh column
   *   must carry a new id: replacing `createPatient:new` with `createPatient:new` is no
   *   URL change at all, so nothing would remount, and even forced through the strip
   *   keys a column by `type:index` and would hand the outgoing component back — still
   *   holding references into the state that just moved into the zone. One slot behind
   *   two live panels means a keystroke in either shows up in both.
   * - **Occupied by an UNTOUCHED form** → aim the open at that column's own id, so the
   *   reducer recognises it as already open and re-triggers (pulse, and unfold if it
   *   was collapsed) instead of re-rooting. Without this the mismatch read as "not
   *   open": the column was replaced and the pristine form torn down and rebuilt, and
   *   only the generation AFTER that behaved, because its id was `new` again.
   */
  const graduateBeforeOpen = <T extends SidebarType>(
    event: SidebarOpenEvent<T>,
  ): SidebarOpenEvent<T> | null => {
    if (!draftDefinition(event.type)?.zone) return null;
    const idProp = sidebars.find((s) => s.type === event.type)?.inline?.idProp;
    if (!idProp) return null;
    const openProps = (event.props ?? null) as Record<string, unknown> | null;
    // Only the root create affordance can occupy the slot. A contextual opener
    // carries a prefill instead of the sentinel (the patient dropdown, the
    // dashboard, the waiting room, the appointment panel) and stays a transient
    // overlay with a throwaway slot — unchanged, and deliberately so: those stack
    // over a peek and are not drafts.
    if (!isDraftColumnId(String(openProps?.[idProp] ?? ''))) return null;
    if (usePreferSidebars().value) return null;
    if (!isWorkspaceRoute(getRouter().currentRoute.value)) return null;
    // No zone below the breakpoint to graduate into (ADR-0029 §E), so the active
    // column simply stays where it is and the click re-triggers it. The four tests
    // above stay here rather than joining it: they are unrelated preconditions that
    // happen to guard the same branch, and folding them into the zone predicate would
    // make a grab-bag out of one product rule (§H-6).
    if (!draftZoneAvailable()) return null;

    const open = useCascadeStore().stack.find(
      (column) => column.type === event.type && isDraftColumnId(column.id),
    );
    if (!open) return null;

    const draftStore = useDraftStore();
    // Untouched: aim at the column that is already there, so this reads as a
    // re-trigger rather than a replacement.
    const graduatedId = draftStore.graduateActiveColumn(event.type);
    const targetId = graduatedId ? draftStore.nextActiveColumnId(event.type) : open.id;
    if (targetId === openProps?.[idProp]) return null;
    return {
      ...event,
      props: { ...openProps, [idProp]: targetId } as SidebarOpenEvent<T>['props'],
    };
  };

  /**
   * An IDENTIFIED type (`DraftDefinition.keyFor`) whose draft is already parked in the
   * zone answers the click itself: the rail unfolds and the draft pulses instead of a
   * second column opening.
   *
   * Not a nicety — the two would be the same draft. The panel keeps its answers keyed
   * by form and patient, so both panels would read and write one set of answers while
   * disagreeing about everything else: which step each is on, and which of them the ✕
   * throws away. The parked one is also the one holding the step the user stopped at,
   * which is the whole reason it was kept.
   */
  const expandParkedDraft = <T extends SidebarType>(event: SidebarOpenEvent<T>): boolean => {
    const definition = draftDefinition(event.type);
    if (!definition?.keyFor) return false;
    const identityKey = definition.keyFor((event.props ?? {}) as Record<string, unknown>);
    if (!identityKey) return false;
    const draftStore = useDraftStore();
    const parked = draftStore.instanceForIdentity(identityKey);
    if (!parked) return false;
    draftStore.setInstanceMinimized(parked.id, false);
    draftStore.pulseInstance(parked.id);
    return true;
  };

  const openSidebar = <T extends SidebarType>(event: SidebarOpenEvent<T>) => {
    if (expandParkedDraft(event)) return;
    // The create slot is occupied by work worth keeping: move it into the draft zone
    // and open the fresh column under the next generation's id.
    routeOrStackSidebar(graduateBeforeOpen(event) ?? event);
  };

  /**
   * The same overlay panel, asked for again while it is the one on screen (TM-9522).
   *
   * An overlay panel is modal: it covers the page with its own scrim, so nothing
   * behind it can ask for a second copy of itself — only the gesture that opened it,
   * fired again. The opener keeps DOM focus behind the scrim (the panel does not take
   * it), so a held or spammed Enter on a "Termin erstellen" button re-clicks it once
   * per keystroke and every one of those pushed another panel, stacking them without
   * limit until the user closed them one by one.
   *
   * Answering the repeat with a no-op makes the overlay path behave like the cascade
   * one, where a second click on an open column is the reducer's own re-trigger rather
   * than a second column (ADR-0029 §G).
   *
   * Identity is the registered `inline.idProp` — the same (type, id) pair
   * `resolveSidebarForClose` matches on — so opening a DIFFERENT record of the same
   * type still stacks (patient A's file over patient B's is a real navigation). A type
   * with no id (`basket`, `videoCallInfo`, `inviteUser`) has only one instance to open,
   * so its type alone settles it; a create panel whose id is absent or the `new`
   * sentinel (`appointmentComposer`, `appointmentDetails` in create mode) compares
   * equal to itself, which is exactly the spammed case.
   */
  const repeatsTopOverlay = <T extends SidebarType>(event: SidebarOpenEvent<T>): boolean => {
    const top = [...sidebarStack.value].reverse().find((s) => !s.closing && !s.inline);
    if (!top || top.sidebarType !== event.type) return false;
    const idProp = sidebars.find((s) => s.type === event.type)?.inline?.idProp;
    if (!idProp) return true;
    const openProps = (event.props ?? null) as Record<string, unknown> | null;
    return (top.props as Record<string, unknown> | null)?.[idProp] === openProps?.[idProp];
  };

  const routeOrStackSidebar = <T extends SidebarType>(event: SidebarOpenEvent<T>) => {
    // In a columnar workspace, an inline-capable sidebar opens as a column in
    // the URL-driven cascade rather than as an overlay. The CascadeColumn
    // mounted by the resulting URL change mirrors itself into sidebarStack
    // with `inline:true` so existing tab/header lookups still work.
    if (shouldRouteToCascade(event)) {
      routeToCascade(event);
      return;
    }

    if (repeatsTopOverlay(event)) return;
    pushOverlay(event);
  };

  const askSidebar = <T extends SidebarType>(
    event: SidebarOpenEvent<T>,
  ): Promise<SidebarCloseEvent<T>> => {
    if (repeatsTopOverlay(event)) {
      const top = [...sidebarStack.value].reverse().find((s) => !s.closing && !s.inline);
      if (top?.answer) return top.answer as unknown as Promise<SidebarCloseEvent<T>>;
    }
    const sidebar = pushOverlay(event);
    sidebar.answer = new Promise((resolve) => {
      sidebar.settle = resolve;
    });
    return sidebar.answer as unknown as Promise<SidebarCloseEvent<T>>;
  };

  const pushOverlay = <T extends SidebarType>(event: SidebarOpenEvent<T>): Sidebar<T> => {
    // Save the current tab view on the current top sidebar before pushing a new one,
    // but only if that view actually belongs to its tabs — otherwise we'd clobber it
    // with a tab name from a sibling sidebar that's mid-close.
    const currentTop = [...sidebarStack.value].reverse().find((s) => !s.closing);
    if (currentTop && currentTop.sidebarTabItems.some((t) => t.view === sidebarTabView.value)) {
      currentTop.activeTabView = sidebarTabView.value;
    }

    const tabItems = event.sidebarTabItems || [];
    const sidebar = new Sidebar<T>(event.type, event.props || null, tabItems);
    sidebarStack.value.push(sidebar);
    if (tabItems.length > 0) {
      sidebarTabView.value = event.initialTabView ?? tabItems[0].view;
    }
    // The stack holds a reactive proxy of what was pushed; hand that back, so a
    // field set on it later (`answer`) is seen by whoever reads the stack.
    return sidebarStack.value[sidebarStack.value.length - 1] as unknown as Sidebar<T>;
  };

  const shouldRouteToCascade = <T extends SidebarType>(event: SidebarOpenEvent<T>): boolean => {
    // Per-device escape hatch (ADR-0021/0027 two-renderer split): when the user
    // has opted to stay with overlay Sidebars everywhere, no *new* open routes
    // into the cascade. This governs opens only — a URL that already encodes
    // columns still hydrates the strip, so deep-links/refresh are unaffected.
    if (usePreferSidebars().value) return false;
    const entry = sidebars.find((s) => s.type === event.type);
    if (!entry?.inline || entry.alwaysOverlay) return false;
    // Inline-capable panels that take an *optional* id (e.g. `appointmentDetails`
    // — appointmentId set = edit, unset = create with rich pre-fill) fall through
    // to overlay when the id is absent. The cascade can only serialize an id-keyed
    // column, so an id-less open is by definition transient and stays overlay.
    const props = (event.props ?? {}) as Record<string, unknown>;
    const idValue = props[entry.inline.idProp];
    if (typeof idValue !== 'string' || idValue.length === 0) return false;
    return isWorkspaceRoute(getRouter().currentRoute.value);
  };

  const routeToCascade = <T extends SidebarType>(event: SidebarOpenEvent<T>): void => {
    const entry = sidebars.find((s) => s.type === event.type);
    if (!entry?.inline) return;
    const props = (event.props ?? {}) as Record<string, unknown>;
    const idValue = props[entry.inline.idProp];
    if (typeof idValue !== 'string' || idValue.length === 0) return;
    const cascadeStore = useCascadeStore();
    // Derive the parent column index: dock under the DEEPEST (rightmost) column
    // whose type is any of the declared `parentIds` types. This realises the
    // registry comment's "the column lands as a child of the closest matching
    // parent column already in the cascade".
    //
    // Nearest-ancestor-of-any-type — not declared-order priority — is deliberate
    // on two counts:
    //  - The same type can legitimately appear more than once in the stack
    //    (e.g. `patientFile(A) → appointmentDetails → patientFile(B)` via the
    //    patientFile↔appointmentDetails parent mapping). Drilling from the
    //    deepest context must dock under that nearest instance (patientFile(B)),
    //    else the child attaches under the outer instance and truncates the
    //    wrong tail.
    //  - Panels with multiple possible parents (e.g. `appointmentComposer` opens
    //    under `prescription` from a plan's task, or under `patientFile` from a
    //    patient; `priceListItemEdit` under `priceListDetails`
    //    or `officeLocationSettings`) no longer depend on `parentIds` order. For
    //    every reachable stack the nested parent (e.g. prescription,
    //    which itself docks under patientFile) is always the deeper one, so this
    //    matches the previous declared-order behaviour — while staying correct
    //    if a hand-crafted deep-link URL ever interleaves the parents the other
    //    way round. `parentIds` order is now purely for id-binding docs.
    // When none match (or no `parentIds`), the column opens at root.
    // A page-level root list forces a re-root (see `openAtRoot`): skip parentIds
    // matching so selecting an item replaces the root and clears the board rather
    // than docking under an open descendant of a matching type.
    const parentTypes = new Set<string>((entry.inline.parentIds ?? []).map((p) => p.type));
    let parentIdx = event.dockUnder
      ? cascadeStore.stack.findIndex(
          (c) => c.type === event.dockUnder!.type && c.id === event.dockUnder!.id,
        )
      : -1;
    if (!event.openAtRoot && parentIdx === -1) {
      for (let i = cascadeStore.stack.length - 1; i >= 0; i--) {
        if (parentTypes.has(cascadeStore.stack[i].type)) {
          parentIdx = i;
          break;
        }
      }
    }
    // Thread the caller's requested tab into the column so a tab-targeted open
    // (e.g. ResourceList opening `officeLocationSettings` on RESSOURCES) lands
    // on that tab instead of the registry `defaultTabView`. Mirror
    // `setColumnTab`'s default-tab omission: drop it when it equals the default
    // so the URL stays `type:id` rather than `type:id@<default>` (the column's
    // `desiredTabView` falls back to the registry default anyway).
    const column: CascadeColumn = { type: event.type, id: idValue };
    const requestedTab = event.initialTabView;
    if (requestedTab !== undefined && requestedTab !== entry.inline.defaultTabView) {
      column.tab = requestedTab;
    }

    // Re-root-under: establish the named ancestor as a fresh root, then dock this
    // panel under it (one navigation). The ancestor opens on this panel's
    // declared `parentTab` (the tab whose list the panel is drilled from) so that
    // list is shown and highlights the open child; omitted when it equals the
    // ancestor's default tab (URL stays `type:id`, desiredTabView falls back).
    if (event.reRootUnder) {
      const ancestorEntry = sidebars.find((s) => s.type === event.reRootUnder!.type);
      const ancestor: CascadeColumn = { type: event.reRootUnder.type, id: event.reRootUnder.id };
      const parentTab = entry.inline.parentTab;
      if (parentTab !== undefined && parentTab !== ancestorEntry?.inline?.defaultTabView) {
        ancestor.tab = parentTab;
      }
      runAfterModalUnwind(() =>
        cascadeStore.openUnderNewRoot(ancestor, column, event.sourceListKey),
      );
      return;
    }

    runAfterModalUnwind(() => cascadeStore.openAt(parentIdx, column, event.sourceListKey));
  };

  /**
   * Which panels have already closed, by the id they were showing then. A double
   * close — the header ✕ and a save racing, a close echoed back by the renderer —
   * would otherwise answer twice: the overlay path has `closing` to stop it, but a
   * column or a draft keeps its mirror in the stack until the URL or the zone
   * unwinds. Keyed by id as well as by mirror, because a column whose id is swapped
   * in place keeps its mirror and is a different record afterwards.
   */
  const closedAs = new WeakMap<object, unknown>();

  const closeSidebar = <T extends SidebarType>(event: SidebarCloseEvent<T>) => {
    const sidebar = (event.sidebar as Sidebar<SidebarType> | undefined) ?? getSidebar(event.type);
    if (!sidebar || sidebar.closing) {
      return;
    }
    const shownId = (sidebar.props as Record<string, unknown> | null)?.[
      idPropOf(sidebar.sidebarType) ?? ''
    ];
    if (closedAs.has(toRaw(sidebar)) && closedAs.get(toRaw(sidebar)) === shownId) return;
    closedAs.set(toRaw(sidebar), shownId);

    // Sent before each placement retires the panel, as it always was, so a listener
    // still finds the panel it is hearing about.
    const answer = (): void => {
      const closed = { ...event, sidebar } as SidebarCloseEvent<SidebarType>;
      closeTrigger.next(closed);
      sidebar.settle?.(closed);
    };

    // A draft-zone panel is mounted from the store, so closing it is a store
    // mutation and not a navigation: retiring the instance unmounts the panel and
    // its mirror. `remove` also throws the entered values away — the guard that
    // asks the user first has already run in GenericSidebarTemplate (or, on the
    // save path, the panel emptied its own slot before calling here).
    if (sidebar.draftInstanceId) {
      answer();
      useDraftStore().remove(sidebar.draftInstanceId);
      return;
    }
    // A panel shown BESIDE a draft (the form editor's "Vorschau"). Closing it retires
    // that one panel and leaves the draft alone — it owns no entered values, so there
    // is nothing to ask about either.
    if (sidebar.draftChildOf) {
      answer();
      useDraftStore().closeChild(sidebar.draftChildOf);
      return;
    }
    // Inline columns are mounted from the URL; closing them must update the URL,
    // which in turn unmounts the column and removes the mirror from sidebarStack.
    if (sidebar.inline) {
      answer();
      const cascadeStore = useCascadeStore();
      const idx = cascadeStore.stack.findIndex(
        (c) => c.type === sidebar.sidebarType && c.id === shownId,
      );
      runAfterModalUnwind(() => cascadeStore.closeColumn(idx === -1 ? 0 : idx));
      return;
    }
    sidebar.closing = true;
    answer();
    setTimeout(() => {
      sidebarStack.value = sidebarStack.value.filter((s) => s !== sidebar);
      // Restore sidebarTabView to the previously active tab of the top remaining sidebar
      const topSidebar = [...sidebarStack.value].reverse().find((s) => !s.closing);
      if (topSidebar && topSidebar.sidebarTabItems.length > 0) {
        sidebarTabView.value = topSidebar.activeTabView || topSidebar.sidebarTabItems[0].view;
      } else if (!topSidebar) {
        sidebarTabView.value = '';
      }
    }, 200);
  };

  const onBackClick = (): Observable<SidebarType> => {
    return backTrigger.asObservable();
  };

  const onSidebarClose = <T extends SidebarType>(
    sidebarType: T,
  ): Observable<SidebarCloseEvent<T>> => {
    return closeTrigger.asObservable().pipe(filter((event) => event.type === sidebarType));
  };

  const onSidebarAnswer = <T extends SidebarType>(
    sidebarType: T,
    origin: Partial<SidebarProps<T>>,
  ): Observable<SidebarCloseEvent<T>> =>
    onSidebarClose(sidebarType).pipe(
      filter((event) => {
        const props = (event.sidebar?.props ?? {}) as Record<string, unknown>;
        return Object.entries(origin).every(([key, value]) => props[key] === value);
      }),
    );

  const setSidebarView = (view: string) => {
    sidebarTabView.value = view;
    const currentTop = [...sidebarStack.value].reverse().find((s) => !s.closing);
    if (currentTop) {
      currentTop.activeTabView = view;
    }
  };

  const store: SidebarStore = {
    openSidebar,
    askSidebar,
    closeSidebar,
    onSidebarAnswer,
    onSidebarClose,
    isSidebarOpen,
    resolveSidebarForClose,
    sidebarStack,
    anySidebarOpen,
    elevatedOverlayOpen,
    updateSidebar,
    onBackClick,
    backTrigger,
    setSidebarView,
    sidebarTabView,
  };
  return store;
});
